import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { pagesData } from '../data/pagesContent';

export default function HomePage() {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en');
  const pageKey = isEn ? 'en' : 'index';
  const page = pagesData[pageKey];

  return (
    <>
      <Helmet>
        <html lang={page.lang} />
        <title>{page.title}</title>
        <meta name="description" content={page.meta.description} />
        <meta property="og:title" content={page.meta.ogTitle} />
        <meta property="og:description" content={page.meta.ogDescription} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/hero.jpg" />
        <link rel="canonical" href={`https://billundvvs.netlify.app${isEn ? '/en/' : '/'}`} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[600px] sm:min-h-[640px] lg:min-h-[680px] flex items-center overflow-hidden">
        {/* Background Image: Montanavej 1B with light Nordic clarity */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/image.png"
            alt={isEn ? "Billund Varme & Sanitet" : "Billund Varme & Sanitet"}
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-0 sm:px-2 lg:px-4 pt-10 pb-36 sm:pt-16 sm:pb-36 lg:pt-20 lg:pb-36 w-full">
          <div className="max-w-2xl px-4 sm:px-6 lg:px-0 -ml-0 sm:-ml-2 lg:-ml-10 xl:-ml-20">
            {/* Hero H1 Display */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] drop-shadow-[0_3px_10px_rgba(0,0,0,0.65)] [text-shadow:_0_2px_14px_rgba(0,0,0,0.6)]">
              {isEn ? "Your local plumber" : "Din lokale VVS'er"}
            </h1>

            {/* Hero H2 / Subline */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight mt-1 sm:mt-2 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)] [text-shadow:_0_2px_10px_rgba(0,0,0,0.5)]">
              {isEn ? "in Billund and surroundings" : "i Billund og omegn"}
            </h2>

            {/* Hero Body / Tagline */}
            <p className="mt-4 sm:mt-5 text-base sm:text-lg text-white font-medium leading-relaxed max-w-xl drop-shadow-[0_1px_6px_rgba(0,0,0,0.7)]">
              {isEn
                ? "Quality, experience and dependable service – since 1963."
                : "Kvalitet, erfaring og pålidelig service – siden 1963."}
            </p>

            {/* Hero CTA Pill Button (Chevron Indicator) */}
            <div className="mt-7 sm:mt-8 flex items-center gap-4">
              <Link
                to={isEn ? '/en/contact' : '/kontakt'}
                className="inline-flex items-center gap-3 px-6 sm:px-7 py-3.5 rounded-full bg-[#d11a1a] hover:bg-[#b51515] text-white font-semibold text-base shadow-[0_4px_14px_0_rgba(209,26,26,0.35)] transition-all active:scale-95 group"
              >
                <span>{isEn ? 'Contact us' : 'Kontakt os'}</span>
                <svg className="w-4 h-4 stroke-[3] group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Trust / Value Proposition Bar (Design Language Spec) */}
        <div className="absolute bottom-0 inset-x-0 bg-black/20 backdrop-blur-xs border-t border-white/10 py-5 sm:py-6 px-4 sm:px-6 lg:px-8 z-20">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/25">
            {/* Item 1 */}
            <div className="flex items-center gap-3.5 px-3 sm:px-4 py-2.5 md:py-0">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white shrink-0 drop-shadow-sm" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
              </svg>
              <span className="text-white text-sm sm:text-base font-semibold leading-snug">
                {isEn ? 'More than 60 years of experience' : 'Mere end 60 års erfaring'}
              </span>
            </div>

            {/* Item 2 */}
            <div className="flex items-center gap-3.5 px-3 sm:px-4 py-2.5 md:py-0">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white shrink-0 drop-shadow-sm" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
              </svg>
              <span className="text-white text-sm sm:text-base font-semibold leading-snug">
                {isEn ? 'Quality and reliability' : 'Kvalitet og pålidelighed'}
              </span>
            </div>

            {/* Item 3 */}
            <div className="flex items-center gap-3.5 px-3 sm:px-4 py-2.5 md:py-0">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white shrink-0 drop-shadow-sm" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" />
              </svg>
              <span className="text-white text-sm sm:text-base font-semibold leading-snug">
                {isEn ? 'Everything within plumbing' : 'Alt inden for VVS'}
              </span>
            </div>

            {/* Item 4 */}
            <div className="flex items-center gap-3.5 px-3 sm:px-4 py-2.5 md:py-0">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white shrink-0 drop-shadow-sm" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" />
              </svg>
              <span className="text-white text-sm sm:text-base font-semibold leading-snug">
                {isEn ? 'Focus on sustainable solutions' : 'Fokus på bæredygtige løsninger'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 3-Column Company Overview Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-12 items-start">
            {/* Column 1: Nybyggeri */}
            <div className="flex flex-col justify-between h-full">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 leading-snug min-h-[3.5rem] flex items-start">
                  {isEn ? 'Plumbing for all types of new construction' : 'VVS i alle former for nybyggeri'}
                </h3>
                <div className="rounded-xl overflow-hidden border border-slate-200 mb-4 shadow-2xs group relative aspect-16/10">
                  <img
                    src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/nybyggeri-vvs-installation.jpg"
                    alt={
                      isEn
                        ? 'Modern residential new construction with plumbing by Billund Varme & Sanitet'
                        : 'Nybyggeri af moderne boliger med VVS-installationer udført af Billund Varme & Sanitet'
                    }
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute bottom-2 left-2 bg-slate-900/80 backdrop-blur-xs text-white text-[11px] font-medium px-2.5 py-1 rounded-md">
                    {isEn ? 'Residential & commercial' : 'Bolig & erhverv'}
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  {isEn
                    ? 'We deliver complete plumbing, heating and ventilation installations for new builds – from private homes and commercial properties to larger housing developments.'
                    : 'Vi udfører komplette VVS-installationer i nybyggeri – fra enfamiliehuse og rækkehuse til erhvervsbyggeri og større entrepriser i hele Trekantområdet.'}
                </p>
              </div>
              <div className="pt-3 border-t border-slate-200">
                <Link
                  to={isEn ? '/en/new-construction' : '/nybyggeri'}
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#d3102e] hover:text-[#a50d26] transition-colors group"
                >
                  <span>{isEn ? 'MORE ABOUT NEW CONSTRUCTION' : 'LÆS MERE OM NYBYGGERI'}</span>
                  <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* Column 2: Velkommen & Værksted */}
            <div className="flex flex-col justify-between h-full">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 leading-snug min-h-[3.5rem] flex items-start">
                  {isEn ? 'Welcome to Billund Varme & Sanitet A/S' : 'Velkommen til Billund Varme & Sanitet A/S'}
                </h3>
                {/* Photo of headquarters building on Montanavej 1B */}
                <div className="rounded-xl overflow-hidden border border-slate-200 mb-4 shadow-2xs group relative aspect-16/10">
                  <img
                    src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/solrig-industriga-rd-med-ra-de-porte-og-varevogne.png"
                    alt={isEn ? "Billund Varme & Sanitet headquarters and workshop on Montanavej 1B in Billund" : "Billund Varme & Sanitet værksted og kontor på Montanavej 1B i Billund"}
                    className="w-full h-full object-cover brightness-[1.02] contrast-[1.02] group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  {isEn
                    ? 'Founded in 1963 and led by authorised installer Mogens Brøgger since 2012. We combine solid craftsmanship, modern green solutions and fast, personal service.'
                    : 'Firmaet har eksisteret siden 1963, og siden 2012 har autoriseret VVS-installatør Mogens Brøgger stået i spidsen. Vi kombinerer solidt håndværk med personlig service og moderne klimaløsninger.'}
                </p>
              </div>
              <div className="pt-3 border-t border-slate-200">
                <Link
                  to={isEn ? '/en/about' : '/om-os'}
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#d3102e] hover:text-[#a50d26] transition-colors group"
                >
                  <span>{isEn ? 'MORE ABOUT US' : 'LÆS MERE OM OS'}</span>
                  <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* Column 3: 2x4 Service Matrix */}
            <div className="flex flex-col justify-between h-full">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 leading-snug min-h-[3.5rem] flex items-start">
                  {isEn ? 'What can we help you with?' : 'Hvad kan vi hjælpe dig med?'}
                </h3>
                <div className="grid grid-cols-2 gap-2.5 mb-6">
                  {(isEn
                    ? [
                        { label: 'PLUMBING', href: '/en/plumbing', icon: 'fa-solid fa-wrench' },
                        { label: 'SHEET METAL', href: '/en/sheet-metal', icon: 'fa-solid fa-ruler-combined' },
                        { label: 'BATHROOMS', href: '/en/plumbing/bathrooms', icon: 'fa-solid fa-shower' },
                        { label: 'HEAT PUMPS', href: '/en/heating/heat-pumps', icon: 'fa-solid fa-fan' },
                        { label: 'DISTRICT HEAT', href: '/en/heating/district-heating', icon: 'fa-solid fa-fire' },
                        { label: 'SERVICE', href: '/en/service-maintenance', icon: 'fa-solid fa-screwdriver-wrench' },
                        { label: 'EMERGENCY', href: '/en/plumbing/emergency', icon: 'fa-solid fa-clock-rotate-left' },
                        { label: 'NEW BUILDS', href: '/en/new-construction', icon: 'fa-solid fa-building' }
                      ]
                    : [
                        { label: 'VVS', href: '/vvs', icon: 'fa-solid fa-wrench' },
                        { label: 'BLIKKENSLAGER', href: '/blikkenslager', icon: 'fa-solid fa-ruler-combined' },
                        { label: 'BADEVÆRELSER', href: '/vvs/badevaerelser', icon: 'fa-solid fa-shower' },
                        { label: 'VARMEPUMPER', href: '/varme/varmepumper', icon: 'fa-solid fa-fan' },
                        { label: 'FJERNVARME', href: '/varme/fjernvarme', icon: 'fa-solid fa-fire' },
                        { label: 'SERVICE', href: '/service-og-vedligehold', icon: 'fa-solid fa-screwdriver-wrench' },
                        { label: 'AKUT VVS', href: '/vvs/akut-vvs', icon: 'fa-solid fa-clock-rotate-left' },
                        { label: 'NYBYGGERI', href: '/nybyggeri', icon: 'fa-solid fa-building' }
                      ]
                  ).map((service, idx) => (
                    <Link
                      key={idx}
                      to={service.href}
                      className="p-3 bg-slate-50/80 hover:bg-red-50/70 border border-slate-200 rounded-xl text-center transition-all group flex flex-col items-center justify-center gap-1.5 shadow-2xs hover:border-red-200"
                    >
                      <i className={`${service.icon} text-slate-600 group-hover:text-[#d3102e] text-sm sm:text-base transition-colors`}></i>
                      <span className="font-bold text-[10px] sm:text-[11px] text-slate-800 group-hover:text-[#d3102e] tracking-wider uppercase transition-colors">
                        {service.label}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
              <div className="pt-3 border-t border-slate-200">
                <Link
                  to={isEn ? '/en/plumbing' : '/vvs'}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#d3102e] hover:bg-[#a50d26] text-white font-bold text-xs uppercase tracking-wider py-3 px-4 rounded-xl transition-colors shadow-2xs"
                >
                  <span>{isEn ? 'SEE ALL SERVICES' : 'SE ALLE YDELSER'}</span>
                  <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary Services Showcase ("Det kan vi hjælpe med") */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-left mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-[#d3102e]">
              {isEn ? 'HOW WE CAN HELP' : 'DET KAN VI HJÆLPE MED'}
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
              {isEn
                ? 'Plumbing, heating and sheet-metal work in Billund – from one local contractor'
                : 'VVS, varme og blikkenslagerarbejde i Billund – fra én lokal leverandør'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* Card 1 */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-7 flex flex-col justify-between shadow-xs">
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  {isEn ? 'Heat pumps and district heating' : 'Varmepumper og fjernvarme'}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {isEn
                    ? 'Time to replace the oil or gas boiler? We advise on air-to-water heat pumps or a district heating connection, apply for grants and handle the whole installation.'
                    : 'Skal olie- eller gasfyret ud? Vi rådgiver om luft til vand-varmepumpe eller fjernvarmetilslutning, søger tilskud og står for hele installationen.'}
                </p>
              </div>
              <div className="pt-6">
                <Link
                  to={isEn ? '/en/heating/heat-pumps' : '/varme/varmepumper'}
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#d3102e] hover:text-[#a50d26]"
                >
                  <span>{isEn ? 'HEAT PUMPS →' : 'VARMEPUMPER →'}</span>
                </Link>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-7 flex flex-col justify-between shadow-xs">
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  {isEn ? 'New bathroom' : 'Nyt badeværelse'}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {isEn
                    ? 'From first sketch to final grout. We coordinate plumbing, electrics, tiling and carpentry so you have one point of contact for the whole renovation.'
                    : 'Fra første skitse til sidste fuge. Vi koordinerer VVS, el, murer og tømrer, så du får ét ansvarligt kontaktpunkt gennem hele renoveringen.'}
                </p>
              </div>
              <div className="pt-6">
                <Link
                  to={isEn ? '/en/plumbing/bathrooms' : '/vvs/badevaerelser'}
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#d3102e] hover:text-[#a50d26]"
                >
                  <span>{isEn ? 'BATHROOMS →' : 'BADEVÆRELSER →'}</span>
                </Link>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-7 flex flex-col justify-between shadow-xs">
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  {isEn ? 'Service agreements for business' : 'Serviceaftaler for erhverv'}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {isEn
                    ? 'Housing associations, institutions and companies get a dedicated technician, annual inspections and fast call-out when something stops working.'
                    : 'Boligforeninger, institutioner og virksomheder får fast tilknyttet montør, årligt eftersyn og hurtig udrykning ved driftstop.'}
                </p>
              </div>
              <div className="pt-6">
                <Link
                  to={isEn ? '/en/commercial' : '/erhverv'}
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#d3102e] hover:text-[#a50d26]"
                >
                  <span>{isEn ? 'COMMERCIAL →' : 'ERHVERV →'}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Area & Working Process Section */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-8 border-b border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Column: Local Coverage */}
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-[#d3102e]">
              {isEn ? 'LOCAL PLUMBER' : "LOKAL VVS'ER"}
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1 mb-5">
              {isEn ? 'We cover all of Billund Municipality and the surrounding area' : 'Vi dækker hele Billund Kommune og omegn'}
            </h2>
            <div className="space-y-4 text-sm text-slate-600 leading-relaxed">
              <p>
                {isEn
                  ? 'Our workshop is on Montanavej in Billund, and our service vans are out every day in Grindsted, Give, Vejle, Sdr. Omme, Vorbasse and Hejnsvig. That means short driving times, fast response and a technician who knows the area.'
                  : 'Vores værksted ligger på Montanavej i Billund, og vores servicebiler kører dagligt i Grindsted, Give, Vejle, Sdr. Omme, Vorbasse og Hejnsvig. Det betyder korte køretider, hurtig udrykning og en montør, der kender området.'}
              </p>
              <p>
                {isEn
                  ? 'Whether you live in a detached house in Billund, run a farm near Vorbasse or manage commercial property in Vejle, you get the same fixed price and the same quality.'
                  : 'Uanset om du bor i et parcelhus i Billund, driver landbrug ved Vorbasse eller administrerer erhvervsejendomme i Vejle, får du den samme faste pris og den samme kvalitet.'}
              </p>
            </div>

            {/* City links separated by dot */}
            <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap gap-2 text-sm font-semibold text-slate-800">
              {isEn ? (
                <>
                  <Link to="/en/plumber-billund" className="hover:text-[#d3102e] underline">Plumber Billund</Link>
                  <span>·</span>
                  <Link to="/en/plumber-grindsted" className="hover:text-[#d3102e] underline">Grindsted</Link>
                  <span>·</span>
                  <Link to="/en/plumber-give" className="hover:text-[#d3102e] underline">Give</Link>
                  <span>·</span>
                  <Link to="/en/plumber-vejle" className="hover:text-[#d3102e] underline">Vejle</Link>
                  <span>·</span>
                  <Link to="/en/plumber-soender-omme" className="hover:text-[#d3102e] underline">Sdr. Omme & Vorbasse</Link>
                </>
              ) : (
                <>
                  <Link to="/vvs-billund" className="hover:text-[#d3102e] underline">VVS Billund</Link>
                  <span>·</span>
                  <Link to="/vvs-grindsted" className="hover:text-[#d3102e] underline">VVS Grindsted</Link>
                  <span>·</span>
                  <Link to="/vvs-give" className="hover:text-[#d3102e] underline">VVS Give</Link>
                  <span>·</span>
                  <Link to="/vvs-vejle" className="hover:text-[#d3102e] underline">VVS Vejle</Link>
                  <span>·</span>
                  <Link to="/vvs-soender-omme" className="hover:text-[#d3102e] underline">VVS Sdr. Omme & Vorbasse</Link>
                </>
              )}
            </div>
          </div>

          {/* Right Column: 4-Step Timeline */}
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-[#d3102e]">
              {isEn ? 'HOW IT WORKS' : 'SÅDAN FOREGÅR DET'}
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1 mb-8">
              {isEn ? 'From first call to finished job' : 'Fra henvendelse til færdigt arbejde'}
            </h2>

            <div className="space-y-6">
              {(isEn
                ? [
                    {
                      num: '1',
                      title: 'You contact us',
                      desc: 'Call, email or fill in the quote form. We get back to you the same working day.'
                    },
                    {
                      num: '2',
                      title: 'Free site visit',
                      desc: 'We come out, look at the job and advise on the best solution.'
                    },
                    {
                      num: '3',
                      title: 'Fixed-price quote',
                      desc: 'You receive a written quote with a fixed price – no hidden costs.'
                    },
                    {
                      num: '4',
                      title: 'We do the work',
                      desc: 'Authorised technicians, tidy work and an agreed schedule.'
                    }
                  ]
                : [
                    {
                      num: '1',
                      title: 'Du kontakter os',
                      desc: 'Ring, skriv eller udfyld tilbudsformularen. Vi vender tilbage samme arbejdsdag.'
                    },
                    {
                      num: '2',
                      title: 'Gratis besigtigelse',
                      desc: 'Vi kommer ud, ser på opgaven og rådgiver om den bedste løsning.'
                    },
                    {
                      num: '3',
                      title: 'Fast pris på tilbud',
                      desc: 'Du får et skriftligt tilbud med fast pris – uden skjulte omkostninger.'
                    },
                    {
                      num: '4',
                      title: 'Vi udfører arbejdet',
                      desc: 'Autoriserede montører, ryddeligt arbejde og aftalt tidsplan.'
                    }
                  ]
              ).map((step) => (
                <div key={step.num} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#d3102e] text-white flex items-center justify-center font-bold text-sm shrink-0">
                    {step.num}
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-slate-900 leading-snug">{step.title}</h3>
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guides Section ("Gode Råd") */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-left mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-[#d3102e]">
              {isEn ? 'GOOD ADVICE' : 'GODE RÅD'}
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
              {isEn ? 'Latest guides from the plumbing professionals' : 'Seneste guides fra VVS-fagmanden'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* Guide 1 */}
            <Link
              to={isEn ? '/en/guides/heat-pump-cost' : '/guides/hvad-koster-en-varmepumpe'}
              className="bg-white border border-slate-200 rounded-xl p-6 sm:p-7 block hover:shadow-md transition-shadow group"
            >
              <span className="inline-block px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[#d3102e] bg-red-50 rounded-sm mb-3">
                {isEn ? 'HEATING' : 'VARME'}
              </span>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-[#d3102e] transition-colors mb-2.5">
                {isEn ? 'What does a heat pump cost?' : 'Hvad koster en varmepumpe i 2026?'}
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {isEn
                  ? 'What affects the price, which grants you can apply for, and how to get an accurate quote.'
                  : 'Hvad der påvirker prisen, hvilke tilskud du kan søge, og hvordan du får et præcist tilbud.'}
              </p>
            </Link>

            {/* Guide 2 */}
            <Link
              to={isEn ? '/en/guides/frozen-pipes' : '/guides/frostspraengte-roer'}
              className="bg-white border border-slate-200 rounded-xl p-6 sm:p-7 block hover:shadow-md transition-shadow group"
            >
              <span className="inline-block px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[#d3102e] bg-red-50 rounded-sm mb-3">
                {isEn ? 'WINTER' : 'VINTER'}
              </span>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-[#d3102e] transition-colors mb-2.5">
                {isEn ? 'How to avoid frozen and burst pipes' : 'Sådan undgår du frostsprængte rør'}
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {isEn
                  ? 'Five simple precautions before the frost arrives – and what to do if the damage is done.'
                  : 'Fem enkle forholdsregler før frosten kommer – og hvad du gør, hvis skaden er sket.'}
              </p>
            </Link>

            {/* Guide 3 */}
            <Link
              to={isEn ? '/en/guides/district-heating-or-heat-pump' : '/guides/fjernvarme-eller-varmepumpe'}
              className="bg-white border border-slate-200 rounded-xl p-6 sm:p-7 block hover:shadow-md transition-shadow group"
            >
              <span className="inline-block px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[#d3102e] bg-red-50 rounded-sm mb-3">
                {isEn ? 'HEATING' : 'VARME'}
              </span>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-[#d3102e] transition-colors mb-2.5">
                {isEn ? 'District heating or heat pump?' : 'Fjernvarme eller varmepumpe?'}
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {isEn
                  ? 'How to choose the right heat source in Billund Municipality – cost, comfort and climate.'
                  : 'Sådan vælger du den rigtige varmekilde i Billund Kommune – økonomi, komfort og klima.'}
              </p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
